<template>
    <vx-disclosure v-model:active="open" appearance="boxed">
        <template v-slot:header>{{ props.label }} ({{ model.length }})</template>
        <template v-slot:content>
            <div class="flex flex-col gap-2 mb-3 lg:flex-row">
                <div class="lg:w-60" data-test="fallback-row-key">
                    <vx-label>Key</vx-label>
                    <vx-input
                        ref="keyElement"
                        size="lg"
                        v-model="key"
                        placeholder="Attribute key"
                        :disabled="props.disabled || props.limitReached"
                        :error="displayFirstError(validate.key)"
                    ></vx-input>
                </div>
                <div class="flex grow gap-2">
                    <div class="grow" data-test="fallback-row-value">
                        <vx-label>Fallback</vx-label>
                        <vx-input
                            size="lg"
                            v-model="value"
                            placeholder="Fallback value"
                            :disabled="props.disabled || props.limitReached"
                            @keypress.enter.prevent="add()"
                            :error="displayFirstError(validate.value)"
                        ></vx-input>
                    </div>
                    <div class="pt-7 lg:pt-6">
                        <vx-button
                            @click="add()"
                            data-test="fallback-row-add"
                            aria-label="Add fallback"
                            :disabled="props.disabled || props.limitReached"
                            size="lg"
                            type="button"
                        >
                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </div>
            <div v-if="props.limitReached" class="mb-3 text-xs text-slate-500" data-test="fallback-row-limit">
                A team can hold at most {{ MAX_FALLBACKS_PER_TEAM }} fallbacks. Remove one to add another.
            </div>
            <vx-alert v-if="!model.length" :closeable="false">{{ props.emptyMessage }}</vx-alert>
            <vx-list-animated v-else v-model="model" value="key" label="key" :removeable="!props.disabled">
                <template v-slot:item="{ item }">
                    <div class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2">
                        <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.key }}</div>
                        <div class="line-clamp-3 break-all">{{ item.value }}</div>
                    </div>
                </template>
            </vx-list-animated>
        </template>
    </vx-disclosure>
</template>

<script setup lang="ts">
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxDisclosure, VxInput, VxLabel, VxListAnimated } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import type { ValidationRule } from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useFormSubmissionChild } from '~/composables/useFormSubmission';
import { MAX_FALLBACKS_PER_TEAM, MAX_FALLBACK_VALUE_LENGTH, normalizeFallbackKey } from '~/utils/messageVariableFallbacks';
import { csvInjection, customAttributeValue, displayFirstError, liquidStringLiteral } from '~/utils/validation';
import type { MessageVariableFallbackRow } from '~/services/messageVariableFallbacksClient.types';

const model = defineModel<MessageVariableFallbackRow[]>({ required: true });

const props = defineProps<{
    label: string;
    keyRule: ValidationRule;
    keyMaxLength: number;
    emptyMessage: string;
    disabled?: boolean;
    limitReached?: boolean;
}>();

const open = ref(false);
const keyElement = ref<{ field?: HTMLInputElement }>();
const key = ref('');
const value = ref('');
const adding = ref(false);
let addInProgress = false;

const { onBeforeSubmit, offBeforeSubmit } = useFormSubmissionChild();

const validate = useVuelidate(
    {
        key: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(props.keyMaxLength),
            format: props.keyRule,
            csvInjection,
        },
        value: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(MAX_FALLBACK_VALUE_LENGTH),
            format: customAttributeValue,
            csvInjection,
            liquidStringLiteral,
        },
    },
    { key, value }
);

const add = async (): Promise<void> => {
    if (props.disabled || props.limitReached) {
        return;
    }

    addInProgress = true;
    adding.value = true;
    await nextTick();

    if (!(await validate.value.$validate())) {
        addInProgress = false;
        setTimeout(() => {
            adding.value = false;
        }, 3500);

        return;
    }

    const existingIndex = model.value.findIndex((row) => normalizeFallbackKey(row.key) === normalizeFallbackKey(key.value));

    if (existingIndex !== -1) {
        model.value.splice(existingIndex, 1);
    }

    model.value.unshift({ key: key.value, value: value.value });

    key.value = '';
    value.value = '';
    adding.value = false;
    addInProgress = false;
    await nextTick();
    keyElement.value?.field?.focus();
};

// The guard is the add itself, not its error display, so a pair corrected straight after a refused add is still
// saved. A collapsed section opens so a refused pair shows its error.
const submitPending = async (): Promise<void> => {
    if (addInProgress || (!key.value && !value.value)) {
        return;
    }

    open.value = true;
    await add();
};

onMounted(() => {
    onBeforeSubmit(submitPending);
});

onUnmounted(() => {
    offBeforeSubmit(submitPending);
});
</script>
